import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiPencil, HiTrash } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";

interface DangerousItem {
  id: number;
  name: string;
  price: number;
}

export const DangerousItemIndex: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, i18n } = useTranslation('dangerousItems');
  const isRTL = i18n.language === 'ar';

  const [items, setItems] = useState<DangerousItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    });

    return params.toString();
  };

  const fetchItems = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/dangerousitem?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('messages.fetchError'));
        return;
      }

      setItems(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error: any) {
      showErrorToast(t('messages.fetchError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize);
  }, [token, currentPage, pageSize, filters]);

  const deleteItem = async (id: number) => {
    if (!window.confirm(t('messages.deleteConfirm'))) return;

    try {
      const response = await adminApi.call(`/dangerousitem/${id}`, 'DELETE');

      if (!response.success) {
        showErrorToast(response.error || t('messages.deleteError'));
        return;
      }

      showSuccessToast(t('messages.deleteSuccess'));
      fetchItems(currentPage, pageSize);
    } catch (error: any) {
      showErrorToast(t('messages.deleteError') + ": " + error.message);
    }
  };

  const handleCreateItem = () => {
    navigate('/dangerous-items/create');
  };

  const handleEditItem = (id: number) => {
    navigate(`/dangerous-items/edit/${id}`);
  };

  const columns: Column[] = [
    {
      key: 'name',
      title: t('table.name'),
      sortable: true,
      filterable: true,
      filterKey: 'name',
    },
    {
      key: 'price',
      title: t('table.price'),
      sortable: true,
      render: (value) => typeof value === 'number' ? `$${value.toFixed(2)}` : value,
    },
    {
      key: 'actions',
      title: t('table.actions'),
      sortable: false,
      width: '150px',
      align: 'center',
      render: (_, item) => (
        <div className="action-buttons">
          {hasPermission("Update Category") && (
            <ActionButton
              variant="warning"
              size="sm"
              icon={HiPencil}
              onClick={() => handleEditItem(item.id)}
              tooltip={t('actions.edit')}
            />
          )}
          {hasPermission("Delete Category") && (
            <ActionButton
              variant="danger"
              size="sm"
              icon={HiTrash}
              onClick={() => deleteItem(item.id)}
              tooltip={t('actions.delete')}
            />
          )}
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    { label: t('breadcrumbs.dangerousItems') }
  ];

  return (
    <PageLayout
      title={t('title')}
      subtitle={t('subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={items}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={true}
        refreshable={true}
        addButton={hasPermission("Create Category") ? {
          label: t('actions.add'),
          onClick: handleCreateItem
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchItems(currentPage, pageSize)}
        emptyMessage={t('messages.noItems')}

      />
    </PageLayout>
  );
};
